import { Color, Vector2 } from 'three';

import glslSdRect from '@resn/gozer-glsl/sdf/sdRect.glsl';
import { CustomMaterial } from '@resn/gozer-three/materials';

import {
    shaderChunks as radialPulseChunks,
    uniforms as radialPulseUniforms,
} from '@/components/gl/shaders/ShaderChunks/radial-pulse-gradient';
import glslFill from '@/libs/lygia/draw/fill.glsl';

const TAU = Math.PI * 2;

export const borderGlowMaterial = ({
    color = '#ff0000',
    brightness = 1,
    scale = 1,
    blurLevel = 1,
    rotateSpeed = 1,
    cornerThreshold = -0.2,
    /** Full angular width of the lit wedge in radians; use ~6.28 for full circle (no wedge). */
    coneSpread = TAU,
    /** 0 = hard edge at spread, 1 = soft falloff across the full half-spread. */
    coneFalloff = 0.25,
    /** Radial pulse: 0..1 expands the mask from center; 1 = fully grown past the frame (with defaults). */
    pulseProgress = 1,
    /** Radial width of the pulse band; larger values fill the screen earlier (see ShaderChunks/radial-pulse-gradient.js). */
    pulseThickness = 2,
    /** Softness of inner/outer edges of the pulse band. */
    pulseFalloff = 0.06,
    /** Radius at pulseProgress 1; tune so the wave exits the viewport. */
    pulseMaxReach = 1,
    /** UV origin of the pulse; screen center is (0.5, 0.5). */
    pulseCenter = new Vector2(0.5, 0.5),
    /** width/height of the quad for a circular pulse on rectangular viewports; 1 disables correction. */
    pulseAspect = 1,
} = {}) => {
    const material = new CustomMaterial({
        options: {
            // blending: AdditiveBlending, // import from 'three' if enabled
            depthWrite: false,
            depthTest: false,
        },
        uniforms: {
            uTime: 0,
            uColor: new Color(color),
            uBrightness: brightness,
            uScale: scale,
            uBlurLevel: blurLevel,
            uRotateSpeed: rotateSpeed,
            uCornerThreshold: cornerThreshold,
            uConeSpread: coneSpread,
            uConeFalloff: coneFalloff,
            ...radialPulseUniforms({
                pulseProgress,
                pulseThickness,
                pulseFalloff,
                pulseMaxReach,
                pulseCenter,
                pulseAspect,
            }),
        },
        vs: /* glsl */ `
            varying vec2 vUv;
            varying vec4 vClipPosition;
            void main() {
                vUv = uv;
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                gl_Position = projectionMatrix * mvPosition;
                vClipPosition = gl_Position;
            }
        `,
        fs: /* glsl */ `
            #define TAU 6.28318530718
            #define PI 3.14159265359

            uniform vec3 uColor;
            uniform float uBrightness;
            uniform float uScale;
            uniform float uBlurLevel;
            uniform float uRotateSpeed;
            uniform float uCornerThreshold;
            uniform float uConeSpread;
            uniform float uConeFalloff;
            uniform float uTime;
            
            ${radialPulseChunks.uniforms}

            varying vec2 vUv;
            varying vec4 vClipPosition;

            ${radialPulseChunks.functions}
            ${glslSdRect}
            ${glslFill}

            float coneMask(vec2 uv) {
                vec2 pc = uv - 0.5;
                if (dot(pc, pc) < 1e-10) {
                    return 1.0;
                }
                float theta = atan(pc.y, pc.x);
                float rot = uRotateSpeed * TAU;
                float dist = abs(mod(theta - rot + PI, TAU) - PI);
                if (uConeSpread >= TAU * 0.999) {
                    return 1.0;
                }
                float halfSpread = uConeSpread * 0.5;
                halfSpread = max(halfSpread, 1e-4);
                float soft = max(1e-4, clamp(uConeFalloff, 0.0, 1.0) * halfSpread);
                return 1.0 - smoothstep(halfSpread - soft, halfSpread, dist);
            }

            void main() {
                float sdf = sdRect(vUv, uScale, uCornerThreshold);
                float shape = fill(sdf, 1.0, uBlurLevel);// * 0.2;
                shape = clamp(1.0 - shape, 0.0, 1.0);


                float borderMask = coneMask(vUv);
                float borderAlpha = shape * uBrightness * borderMask;
                vec4 borderGlow = vec4(uColor, borderAlpha);


                // Pulse effect
                vec2 ndc = vClipPosition.xy / max(vClipPosition.w, 1e-5);
                vec2 screenUv = ndc * 0.5 + 0.5;
                float pulseMask = radialPulseGradient(
                    screenUv,
                    uPulseProgress,
                    uPulseThickness,
                    uPulseFalloff,
                    uPulseMaxReach
                );
                float pulseAlpha = pulseMask * smoothstep(0.0, 0.1, uPulseProgress) * 0.2;
                vec4 pulse = vec4(uColor, pulseAlpha);


                // gl_FragColor = borderGlow;
                gl_FragColor = borderGlow + pulse;
                // gl_FragColor = pulse;
                // gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
                // gl_FragColor = vec4(vec3(uColor), 1.0);
            }
        `,
    });

    return material;
};
